import { useEffect, useRef, useState } from "react";
import type { SubjectDTO, SubjectTypeDTO } from "../../shared/types";
import { get } from "../lib/api";
import { scopeName, shortId } from "../lib/scopes";
import { Icon } from "./Icon";
import { TextLink } from "./TextLink";
import { inputCls } from "./ui";

/** Finds a scope of the given types by name or by any tool's id, and shows the one picked as "Type · Name". */
export function ScopePicker({ types, value, onChange, placeholder = "Search scopes by name or id" }: {
  types: SubjectTypeDTO[]; value: SubjectDTO | null; onChange: (scope: SubjectDTO | null) => void; placeholder?: string;
}) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [found, setFound] = useState<SubjectDTO[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const root = useRef<HTMLDivElement>(null);
  const kinds = types.map((t) => t.key).join(",");
  const typeName = (kind: string) => types.find((t) => t.key === kind)?.name ?? kind;

  useEffect(() => {
    if (!open || !kinds) return;
    let current = true;
    // A few per type, searched as the person types; each type is asked on its own so one big type cannot crowd out the rest.
    const timer = setTimeout(async () => {
      try {
        const pages = await Promise.all(
          kinds.split(",").map((kind) => get<{ items: SubjectDTO[] }>(`/api/subjects?${new URLSearchParams({ kind, paged: "1", limit: "8", ...(q.trim() ? { q: q.trim() } : {}) })}`)),
        );
        if (!current) return;
        setFound(pages.flatMap((p) => p.items));
        setError(null);
      } catch (err) {
        if (current) setError((err as Error).message);
      }
    }, 200);
    return () => {
      current = false;
      clearTimeout(timer);
    };
  }, [open, q, kinds]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!root.current?.contains(e.target as Node)) setOpen(false);
    };
    // Escape closes the list alone: a dialog it sits in listens on window, after this, and stays open.
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.stopPropagation();
      setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (value) {
    return (
      <div className="flex items-center gap-2 rounded-xl bg-surface px-3 py-2 text-sm shadow-sm ring-1 ring-line">
        <Icon name="scopes" className="size-4 shrink-0 text-faint" />
        <span className="min-w-0 flex-1 truncate">
          <span className="text-muted">{typeName(value.kind)} · </span>
          {scopeName(value)}
        </span>
        <TextLink className="shrink-0 text-xs" onClick={() => onChange(null)}>
          Change
        </TextLink>
      </div>
    );
  }
  return (
    <div ref={root} className="relative">
      <input
        className={inputCls}
        value={q}
        placeholder={placeholder}
        onFocus={() => setOpen(true)}
        onChange={(e) => {
          setQ(e.target.value);
          setOpen(true);
        }}
        aria-label="Search scopes"
      />
      {open && (
        <div role="listbox" className="card absolute left-0 z-30 mt-1 max-h-72 w-full overflow-auto p-1 shadow-lg">
          {error ? (
            <p className="px-3 py-2 text-xs text-danger">{error}</p>
          ) : found === null ? (
            <p className="px-3 py-2 text-xs text-muted">Searching…</p>
          ) : found.length === 0 ? (
            <p className="px-3 py-2 text-xs text-muted">{q.trim() ? "No scope matches." : "No scopes yet."}</p>
          ) : (
            found.map((s) => (
              <button
                key={s.id}
                type="button"
                role="option"
                aria-selected={false}
                onClick={() => {
                  onChange(s);
                  setOpen(false);
                  setQ("");
                }}
                className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm hover:bg-fill"
              >
                <span className="min-w-0 flex-1 truncate">
                  <span className="text-muted">{typeName(s.kind)} · </span>
                  {scopeName(s)}
                </span>
                <span className="shrink-0 font-mono text-tiny text-faint">{shortId(s.slug)}</span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
